/**
 * React Query hooks for field preparation events.
 * React Query hooks for the corresponding entity.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import {
  FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS,
  FieldPreparationEvent,
} from "./fieldPreparationEventTypes";

// Re-export types for convenience
export {
  FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS,
  fieldPreparationEventApi,
};
export type { FieldPreparationEvent };

class FieldPreparationAPI extends API<FieldPreparationEvent> {
  async getAllFieldPreparationEventsForFarm(
    farmGuid: string,
    fieldPreparationEventTimeAfter?: string
  ): Promise<FieldPreparationEvent[]> {
    const fieldPreparationEventTimeAfterParam = fieldPreparationEventTimeAfter
      ? `&fieldPreparationEventTimeAfter=${fieldPreparationEventTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}${this.entity}?farmGuid=${farmGuid}${fieldPreparationEventTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as FieldPreparationEvent[];
  }
}

const fieldPreparationEventApi = new FieldPreparationAPI("fieldPreparation");

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all field preparation events for a farm
 */
export function useFieldPreparationEvents(
  farmGuid: string | undefined,
  fieldPreparationEventTimeAfter?: string
) {
  return useQuery({
    queryKey: [
      ...queryKeys.events.fieldPreparation.all,
      farmGuid,
      fieldPreparationEventTimeAfter,
    ],
    queryFn: () =>
      fieldPreparationEventApi.getAllFieldPreparationEventsForFarm(
        farmGuid ?? "",
        fieldPreparationEventTimeAfter
      ),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single field preparation event by guid
 */
export function useFieldPreparationEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["fieldPreparationEvent", guid],
    queryFn: () => fieldPreparationEventApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Get all field preparation events for a specific field
 */
export function useFieldPreparationEventsForField(
  farmGuid: string | undefined,
  fieldGuid: string | undefined,
  fieldPreparationEventTimeAfter?: string
) {
  const { data: allEvents, ...rest } = useFieldPreparationEvents(
    farmGuid,
    fieldPreparationEventTimeAfter
  );

  const filteredEvents = allEvents?.filter(
    (event) => fieldGuid && event.fieldGuids.includes(fieldGuid)
  );

  return {
    data: filteredEvents,
    ...rest,
  };
}

/**
 * Create a new field preparation event
 */
export function useCreateFieldPreparationEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fieldPreparationEvent,
    }: {
      fieldPreparationEvent: FieldPreparationEvent;
      farmGuid: string;
    }) => {
      const result = await fieldPreparationEventApi.createOne(
        fieldPreparationEvent
      );
      return { ...fieldPreparationEvent, ...result };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fieldPreparation.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fieldPreparation"],
      });
    },
  });
}

/**
 * Update an existing field preparation event
 */
export function useUpdateFieldPreparationEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fieldPreparationEvent,
    }: {
      fieldPreparationEvent: FieldPreparationEvent;
    }) => {
      const result = await fieldPreparationEventApi.updateOne(
        fieldPreparationEvent
      );
      return { ...fieldPreparationEvent, ...result };
    },
    onSuccess: async (updatedEvent) => {
      queryClient.setQueryData(
        ["fieldPreparationEvent", updatedEvent.guid],
        updatedEvent
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fieldPreparation.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fieldPreparation"],
      });
    },
  });
}

/**
 * Delete a field preparation event
 */
export function useDeleteFieldPreparationEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      await fieldPreparationEventApi.deleteOne(guid);
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["fieldPreparationEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fieldPreparation.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fieldPreparation"],
      });
    },
  });
}
